import { useEffect, useMemo, useState } from "react";
import { Loader2, RadioTower } from "lucide-react";
import { AdminPage } from "@/components/AdminPage";
import { useToast } from "@/components/Toast";
import { api } from "@/lib/api";
import { mapAdminApiError } from "@/lib/errors";
import type { ApiContract } from "@podecontratar/shared/contract/index";
import type { EndpointOutput } from "@podecontratar/shared/contract/endpoint";

type EventSetting = EndpointOutput<ApiContract["GET /admin/events"]>[number];

/**
 * Category order and copy. The keys are the API's categories; the order
 * here is editorial — the ones an operator is most likely to want to
 * quieten sit at the top.
 */
const CATEGORIES: ReadonlyArray<{ key: string; label: string; hint: string }> = [
  { key: "TRACK", label: "Entrega de mensagens", hint: "Retorno do provedor sobre e-mails e mensagens enviadas" },
  { key: "AUTH", label: "Autenticação", hint: "Cadastro, login, recuperação de senha" },
  { key: "PROFILE", label: "Perfil", hint: "E-mails, telefones, endereços e empresas do usuário" },
  { key: "VERIFICATION", label: "Consultas", hint: "Ciclo de vida das verificações" },
  { key: "BILLING", label: "Cobrança", hint: "Checkout, assinatura, pagamento" },
  { key: "MONITORING", label: "Monitoramento", hint: "Resultados e resumos de monitoramento" },
  { key: "SUBJECT", label: "Titulares", hint: "Quem foi consultado e o que fez a respeito" },
  { key: "REFERRAL", label: "Indicações", hint: "Programa de indicação" },
  { key: "LEAD", label: "Leads", hint: "Formulários e captação" },
  { key: "WEB", label: "Web", hint: "Sinais do site" },
];

function Switch({
  checked,
  onChange,
  label,
  busy,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={busy}
      onClick={() => onChange(!checked)}
      className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 disabled:cursor-not-allowed disabled:opacity-50"
      // Inline rather than utilities, matching the painel's toggle. The
      // knob's offset is a transform, and Tailwind v4 composes those
      // through `translate: var(--tw-translate-x) var(--tw-translate-y)`
      // — one undefined half drops the whole declaration and the knob
      // never moves.
      style={{ backgroundColor: checked ? "var(--color-accent)" : "var(--color-border)" }}
    >
      <span
        className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-surface shadow-sm transition-transform"
        style={{ transform: checked ? "translateX(20px)" : "translateX(0)" }}
      />
    </button>
  );
}

export default function AdminEventsPage() {
  const [events, setEvents] = useState<EventSetting[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  async function refresh() {
    try {
      const { items } = await api.request("GET /admin/events");
      setEvents(items);
      setError(null);
    } catch {
      setError("Não foi possível carregar os eventos.");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  const byCategory = useMemo(() => {
    const map = new Map<string, EventSetting[]>();
    for (const e of events ?? []) {
      const list = map.get(e.category);
      if (list) list.push(e);
      else map.set(e.category, [e]);
    }
    return map;
  }, [events]);

  const offCount = (events ?? []).filter((e) => !e.enabled).length;

  /**
   * Optimistic: a toggle that waits for the round-trip feels broken, and
   * the failure path restores the server's answer rather than guessing at
   * a rollback.
   */
  async function apply(input: ReadonlyArray<{ event: string; enabled: boolean }>) {
    // Second guard, after the disabled switches: a stale page must not
    // be able to send a request the API will refuse.
    const locked = new Set(events?.filter((e) => e.locked).map((e) => e.event));
    const changes = input.filter((c) => !locked.has(c.event));
    if (!events || saving || changes.length === 0) return;
    const wanted = new Map(changes.map((c) => [c.event, c.enabled]));
    const previous = events;

    setEvents(events.map((e) => (wanted.has(e.event) ? { ...e, enabled: wanted.get(e.event)! } : e)));
    setSaving(true);
    try {
      await api.request("PATCH /admin/events", { body: { events: changes } });
      await refresh();
    } catch (err) {
      setEvents(previous);
      toast.push(mapAdminApiError(err, "Não foi possível salvar a alteração."), "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPage
      title="Eventos"
      description="O que o backend envia para o Novu. Desligar um evento interrompe só o envio das jornadas que ele dispara — os cancelamentos de jornadas que ele encerra continuam acontecendo. Os marcados como “envia mensagem” nomeiam um e-mail transacional e não têm jornada própria para desligar — o interruptor deles é o do workflow, no painel do Novu."
    >
      {error ? (
        <div className="rounded-3xl border border-danger/30 bg-danger/10 p-5 text-sm text-text">
          {error}
        </div>
      ) : events === null ? (
        <div className="grid place-items-center rounded-3xl border border-border bg-surface py-16">
          <Loader2 size={20} className="animate-spin text-text-tertiary" aria-hidden />
        </div>
      ) : (
        <>
          <p className="-mt-4 mb-6 flex items-center gap-1.5 text-xs text-text-tertiary">
            <RadioTower size={13} strokeWidth={2} aria-hidden />
            {offCount === 0
              ? `Os ${events.length} eventos estão ligados.`
              : `${offCount} de ${events.length} eventos desligados.`}
            {" "}Uma alteração vale em toda a plataforma em cerca de um minuto.
          </p>

          <div className="space-y-4">
            {CATEGORIES.map((cat) => {
              const list = byCategory.get(cat.key);
              if (!list?.length) return null;
              // Locked events are neither counted nor toggled: a
              // category made only of them would otherwise render as
              // permanently half-off.
              const switchable = list.filter((e) => !e.locked);
              const allOn = switchable.length > 0 && switchable.every((e) => e.enabled);

              return (
                <section
                  key={cat.key}
                  className="overflow-hidden rounded-3xl border border-border bg-surface"
                >
                  <header className="flex items-center gap-3 border-b border-border px-5 py-3.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium tracking-tight text-text">
                        {cat.label}
                        <span className="ml-2 text-xs font-normal text-text-tertiary">
                          {list.filter((e) => e.enabled).length}/{list.length}
                        </span>
                      </p>
                      <p className="truncate text-xs text-text-tertiary">{cat.hint}</p>
                    </div>
                    <Switch
                      checked={allOn}
                      busy={saving || switchable.length === 0}
                      label={`${allOn ? "Desligar" : "Ligar"} todos os eventos de ${cat.label}`}
                      onChange={(next) =>
                        apply(
                          switchable
                            .filter((e) => e.enabled !== next)
                            .map((e) => ({ event: e.event, enabled: next }))
                        )
                      }
                    />
                  </header>

                  <ul className="divide-y divide-border">
                    {list.map((e) => (
                      <li key={e.event} className="flex items-center gap-3 px-5 py-2.5">
                        <code
                          className={`min-w-0 flex-1 truncate font-mono text-xs ${
                            e.enabled ? "text-text-secondary" : "text-text-tertiary line-through"
                          }`}
                        >
                          {e.event}
                        </code>
                        {e.locked && (
                          <span className="shrink-0 text-[11px] text-text-tertiary">
                            envia mensagem
                          </span>
                        )}
                        <Switch
                          checked={e.enabled}
                          busy={saving || e.locked}
                          label={
                            e.locked
                              ? `${e.event} nomeia um envio e não tem jornada para desligar`
                              : `${e.enabled ? "Desligar" : "Ligar"} ${e.event}`
                          }
                          onChange={(next) => apply([{ event: e.event, enabled: next }])}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        </>
      )}
    </AdminPage>
  );
}
